import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Signal, SignalType } from "@types";
import { signOut } from "@store/auth";
import { getUpdatedSignalsList } from "@utils";
import { DateRangeDefaults, SignalsState } from "./signals.types";
import {
  createPixel,
  getSignals,
  getThreadAccessEvents,
  getThreadTrackStatus,
  updateReadSignals,
  updateThreadTrackStatus,
  updateViewedSignals,
} from "./signals.thunks";

const initialState: SignalsState = {
  isLoading: {
    signals: false,
    threadAccessEvents: false,
    viewedSignals: false,
    readSignals: false,
    threadTrackStatus: false,
    pixel: false,
    emailSentEvent: false,
  },
  isError: {
    signals: false,
    threadAccessEvents: false,
    viewedSignals: false,
    readSignals: false,
    threadTrackStatus: false,
    pixel: false,
    emailSentEvent: false,
  },
  pagination: {
    page: 0,
    count: 0,
    ipp: 0,
    nextPage: null,
    notViewedSignalsCount: 0,
    unreadSignalsCount: 0,
  },
  filters: {
    dateRangeString: DateRangeDefaults.All,
    eventName: [],
  },
  results: [],
  selectedSignal: null,
  pixel: null,
};

const signalsSlice = createSlice({
  initialState,
  name: "signals",
  reducers: {
    selectSignal: (state, action: PayloadAction<string | null>) => {
      state.selectedSignal = action.payload
        ? state.results.find((signal) => signal.eventId === action.payload) || null
        : null;
    },
    updateSignalsFilters: (
      state,
      action: PayloadAction<{
        dateRangeString?: string;
        eventName?: SignalType[];
      }>
    ) => {
      const {
        dateRangeString = state.filters.dateRangeString,
        eventName = state.filters.eventName,
      } = action.payload;

      state.filters.dateRangeString = dateRangeString;
      state.filters.eventName = eventName;
      state.pagination = initialState.pagination;
      state.results = [];
    },
    pushSignal: (state, action: PayloadAction<Signal>) => {
      const { eventCreatedAt, ...signal } = action.payload;
      const signalsFromResponse = getUpdatedSignalsList(state.results, [
        {
          ...signal,
          eventDate: eventCreatedAt || new Date().toISOString(),
        },
      ]);
      state.results = [...signalsFromResponse, ...state.results];
      state.pagination.unreadSignalsCount += signalsFromResponse.length;
      state.pagination.notViewedSignalsCount += signalsFromResponse.length;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getSignals.pending, (state) => {
      state.isError.signals = false;
      state.isLoading.signals = true;
    });
    builder.addCase(getSignals.fulfilled, (state, action) => {
      state.isLoading.signals = false;
      state.results = [...state.results, ...action.payload.results];
      state.pagination = action.payload.pagination;
    });
    builder.addCase(getSignals.rejected, (state, action) => {
      state.isError.signals = !action.meta.aborted;
      state.isLoading.signals = false;
    });
    builder.addCase(getThreadAccessEvents.pending, (state) => {
      state.isError.threadAccessEvents = false;
      state.isLoading.threadAccessEvents = true;
    });
    builder.addCase(getThreadAccessEvents.fulfilled, (state, action) => {
      state.isLoading.threadAccessEvents = false;

      if (state.selectedSignal?.eventName === SignalType.EmailOpened) {
        state.selectedSignal.eventMetadata.accessEvents = {
          ...action.payload.pagination,
          results: [
            ...state.selectedSignal.eventMetadata.accessEvents.results,
            ...action.payload.results,
          ],
        };
      }
    });
    builder.addCase(getThreadAccessEvents.rejected, (state, action) => {
      state.isError.threadAccessEvents = !action.meta.aborted;
      state.isLoading.threadAccessEvents = false;
    });
    builder.addCase(updateReadSignals.pending, (state) => {
      state.isError.readSignals = false;
      state.isLoading.readSignals = true;
    });
    builder.addCase(updateReadSignals.fulfilled, (state, action) => {
      const eventIdsSet = new Set(action.payload);
      state.isLoading.readSignals = false;
      state.pagination.unreadSignalsCount = Math.max(
        state.pagination.unreadSignalsCount - action.payload.length,
        0
      );
      state.results = state.results.map((signal) => {
        if (eventIdsSet.has(signal.eventId)) {
          return {
            ...signal,
            read: true,
          };
        }
        return signal;
      });
    });
    builder.addCase(updateReadSignals.rejected, (state, action) => {
      state.isError.readSignals = !action.meta.aborted;
      state.isLoading.readSignals = false;
    });
    builder.addCase(updateViewedSignals.pending, (state) => {
      state.isError.viewedSignals = false;
      state.isLoading.viewedSignals = true;
    });
    builder.addCase(updateViewedSignals.fulfilled, (state) => {
      state.isLoading.viewedSignals = true;
      state.pagination.notViewedSignalsCount = 0;
    });
    builder.addCase(updateViewedSignals.rejected, (state, action) => {
      state.isError.viewedSignals = !action.meta.aborted;
      state.isLoading.viewedSignals = false;
    });
    builder.addCase(getThreadTrackStatus.pending, (state) => {
      state.isError.threadTrackStatus = false;
      state.isLoading.threadTrackStatus = true;
    });
    builder.addCase(getThreadTrackStatus.fulfilled, (state, action) => {
      state.isLoading.threadTrackStatus = false;

      if (
        state.selectedSignal?.eventName === SignalType.EmailOpened &&
        state.selectedSignal.eventMetadata?.thread?.platformThreadId ===
          action.payload.platformThreadId
      ) {
        state.selectedSignal.eventMetadata.thread.onTrack = action.payload.onTrack;
      }
    });
    builder.addCase(getThreadTrackStatus.rejected, (state, action) => {
      state.isError.threadTrackStatus = !action.meta.aborted;
      state.isLoading.threadTrackStatus = false;
    });
    builder.addCase(updateThreadTrackStatus.pending, (state, action) => {
      state.isError.threadTrackStatus = false;
      state.isLoading.threadTrackStatus = true;

      if (
        state.selectedSignal?.eventName === SignalType.EmailOpened &&
        state.selectedSignal.eventMetadata?.thread?.platformThreadId === action.meta.arg.threadId
      ) {
        state.selectedSignal.eventMetadata.thread.onTrack = action.meta.arg.onTrack;
      }
    });
    builder.addCase(updateThreadTrackStatus.fulfilled, (state, action) => {
      state.isLoading.threadTrackStatus = false;

      if (
        state.selectedSignal?.eventName === SignalType.EmailOpened &&
        state.selectedSignal.eventMetadata?.thread?.platformThreadId === action.meta.arg.threadId
      ) {
        state.selectedSignal.eventMetadata.thread.onTrack = action.payload;
      }
    });
    builder.addCase(updateThreadTrackStatus.rejected, (state, action) => {
      state.isError.threadTrackStatus = !action.meta.aborted;
      state.isLoading.threadTrackStatus = false;
    });
    builder.addCase(createPixel.pending, (state) => {
      state.isError.pixel = false;
      state.isLoading.pixel = true;
    });
    builder.addCase(createPixel.fulfilled, (state, action) => {
      state.isLoading.pixel = false;
      state.pixel = action.payload;
    });
    builder.addCase(createPixel.rejected, (state, action) => {
      state.isError.pixel = !action.meta.aborted;
      state.isLoading.pixel = false;
    });
    builder.addCase(signOut.pending, (state) => {
      state.pagination = initialState.pagination;
      state.filters = initialState.filters;
      state.results = null;
      state.selectedSignal = null;
      state.pixel = null;
    });
  },
});

export const { selectSignal, updateSignalsFilters, pushSignal } = signalsSlice.actions;

export const signalsReducer = signalsSlice.reducer;
